import { moveToFulfilled, moveToPending, moveToRejected, remove } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  GroupAddUserFulfilledAction,
  GroupRemoveUserFulfilledAction,
} from '../../domains/groupAddRemoveUser/actions.types';
import type { GroupAddUserByEmailFulfilledAction } from '../../domains/groupAddRemoveUser/addUserByEmailActions.types';
import type {
  GroupMemberSearchLoadAction,
  GroupMemberSearchLoadFulfilledAction,
  GroupMemberSearchRemoveAction,
} from '../../domains/groupMemberSearch/actions.types';
import type { Group, User } from '../types';

import type { GroupMemberSearches } from './types';

export type GroupMemberSearchesAction =
  | GroupMemberSearchLoadAction
  | GroupMemberSearchRemoveAction
  | GroupAddUserFulfilledAction
  | GroupRemoveUserFulfilledAction
  | GroupAddUserByEmailFulfilledAction;

const initialState: GroupMemberSearches = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const listExcludingSpecifiedUser = (users: User['id'][], userId: User['id']) => users.filter((id) => id !== userId);

const stateWithFulfilledSearch = (
  state: GroupMemberSearches,
  payload: GroupMemberSearchLoadFulfilledAction['payload']
): GroupMemberSearches => {
  const { id, searchText, result } = payload;

  const results = {
    [id]: {
      id,
      searchText,
      groupUserIds: result.groupUserIds,
      networkUserIds: result.networkUserIds,
    },
  };

  return moveToFulfilled(state, results);
};

const addUserToGroup = (state: GroupMemberSearches, groupId: Group['id'], userId: User['id']): GroupMemberSearches => {
  const groupMemberSearch = state.fulfilled.byId[groupId];

  if (!groupMemberSearch) {
    return state;
  }

  const updatedGroupMemberSearch = {
    [groupId]: {
      ...groupMemberSearch,
      groupUserIds: [...groupMemberSearch.groupUserIds, userId],
      networkUserIds: listExcludingSpecifiedUser(groupMemberSearch.networkUserIds, userId),
    },
  };

  return moveToFulfilled(state, updatedGroupMemberSearch);
};

const removeUserFromGroup = (
  state: GroupMemberSearches,
  { groupId, userId }: GroupRemoveUserFulfilledAction['payload']
): GroupMemberSearches => {
  const groupMemberSearch = state.fulfilled.byId[groupId];

  if (!groupMemberSearch) {
    return state;
  }

  const updatedGroupMemberSearch = {
    [groupId]: {
      ...groupMemberSearch,
      groupUserIds: listExcludingSpecifiedUser(groupMemberSearch.groupUserIds, userId),
      networkUserIds: [...groupMemberSearch.networkUserIds, userId],
    },
  };

  return moveToFulfilled(state, updatedGroupMemberSearch);
};

const reducer: Reducer<GroupMemberSearches, GroupMemberSearchesAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'GROUP_MEMBER_SEARCH.LOAD_PENDING':
      return moveToPending(state, [action.payload.id]);
    case 'GROUP_MEMBER_SEARCH.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.id], action.payload.error);
    case 'GROUP_MEMBER_SEARCH.LOAD_FULFILLED':
      return stateWithFulfilledSearch(state, action.payload);
    case 'GROUP_MEMBER_SEARCH.QUERY_CLEARED':
      return remove(state, [action.payload.id]);
    case 'GROUP.ADD_USER_FULFILLED':
      return addUserToGroup(state, action.payload.groupId, action.payload.userId);
    case 'GROUP.ADD_USER_BY_EMAIL_FULFILLED':
      return addUserToGroup(state, action.payload.groupId, action.payload.userId);
    case 'GROUP.REMOVE_USER_FULFILLED':
      return removeUserFromGroup(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
